/* eslint-disable ember/no-classic-components, ember/require-tagless-components */
import { tracked } from "@glimmer/tracking";
import Component, { Input } from "@ember/component";
import { fn, hash } from "@ember/helper";
import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import DButton from "discourse/components/d-button";
import DModal from "discourse/components/d-modal";
import { popupAjaxError } from "discourse/lib/ajax-error";
import UserChooser from "discourse/select-kit/components/user-chooser";
import { i18n } from "discourse-i18n";
import CirclesUserDropdown from "../circles/user-dropdown";

export default class CircleManageAdvancedOwnershipModal extends Component {
  @service currentUser;
  @service router;

  @tracked oldOwner;
  @tracked newOwner;
  @tracked removeOldOwnerFromStaff;
  model;

  init() {
    super.init(...arguments);

    if (this.model.circle.owners.length === 1) {
      this.oldOwner = this.model.circle.owners[0]?.username;
    } else {
      this.oldOwner = null;
    }

    this.newOwner = null;
    this.removeOldOwnerFromStaff = true;
  }

  get rawTitle() {
    return i18n(
      `discourse_circles.circle.manage.advanced.ownership.${this.mode}.modal.title`,
      {
        circle_name: this.model.circle.name,
      }
    );
  }

  get mode() {
    return this.model.mode;
  }

  get canChooseOldOwner() {
    return (
      this.mode === "remove" ||
      (this.mode === "transfer" &&
        (this.oldOwner == null || this.model.circle.owners.length > 1))
    );
  }

  get oldLabelText() {
    return i18n(
      `discourse_circles.circle.manage.ownership.remove.modal.message`
    );
  }

  get canChooseNewOwner() {
    return this.mode === "add" || this.mode === "transfer";
  }

  get newLabelText() {
    return i18n(`discourse_circles.circle.manage.ownership.add.modal.message`);
  }

  get groups() {
    return [
      this.model.circle.member_group.name,
      this.model.circle.staff_group.name,
    ];
  }

  get disableSubmit() {
    switch (this.mode) {
      case "add":
        return this.newOwner?.length !== 1;
      case "remove":
        return this.oldOwner == null;
      case "transfer":
        return (
          this.newOwner?.length !== 1 ||
          this.oldOwner == null ||
          this.newOwner?.[0] === this.oldOwner
        );
      default:
        return true;
    }
  }

  @computed("mode", "currentUser", "oldOwner")
  get warningMessage() {
    if (this.oldOwner == null) {
      return;
    }

    switch (this.mode) {
      case "remove":
        return i18n(
          "discourse_circles.circle.manage.advanced.ownership.remove.modal.warning",
          { owner_username: this.oldOwner }
        );
      case "transfer":
        const type =
          this.currentUser.username === this.oldOwner ? "self" : "other";

        return i18n(
          `discourse_circles.circle.manage.advanced.ownership.transfer.modal.warning.${type}`,
          { owner_username: this.oldOwner }
        );
    }
  }

  @computed("mode", "currentUser", "oldOwner")
  get removeStaffMembershipLabel() {
    if (this.oldOwner == null) {
      return;
    }

    switch (this.mode) {
      case "remove":
      case "transfer":
        const type =
          this.currentUser.username === this.oldOwner ? "self" : "other";

        return i18n(
          `discourse_circles.circle.manage.advanced.ownership.remove_staff_membership.${type}`,
          { owner_username: this.oldOwner }
        );
    }
  }

  @action
  async save() {
    try {
      this.saving = true;

      await this.model.circle.changeOwnership(this.mode, this.#apiParams);
      this.close();

      if (!this.model.circle.can_manage_ownership) {
        this.router.transitionTo("circle.activity");
      }
    } catch (e) {
      popupAjaxError(e);
    } finally {
      this.saving = false;
    }
  }

  @action
  close() {
    this.closeModal();
  }

  get #apiParams() {
    switch (this.mode) {
      case "add":
        return {
          username: this.newOwner[0],
        };
      case "remove":
        return {
          username: this.oldOwner,
          remove_from_staff: this.removeOldOwnerFromStaff,
        };
      case "transfer":
        return {
          from_username: this.oldOwner,
          to_username: this.newOwner[0],
          remove_from_staff: this.removeOldOwnerFromStaff,
        };
    }
  }

  <template>
    <DModal
      @closeModal={{@closeModal}}
      @title={{this.rawTitle}}
      @tagName="form"
      class="circles-manage-advanced-ownership-modal"
    >
      <:body>
        {{#if this.canChooseOldOwner}}
          <label
            for="circles-manage-advanced-old-owner-chooser"
            class="control-label"
          >
            {{i18n
              "discourse_circles.circle.manage.advanced.ownership.remove.modal.label"
            }}
          </label>
          <CirclesUserDropdown
            @id="circles-manage-advanced-old-owner-chooser"
            @value={{this.oldOwner}}
            @content={{this.model.circle.owners}}
            @onChange={{fn (mut this.oldOwner)}}
            @options={{hash
              headerAriaLabel=(i18n
                "discourse_circles.circle.manage.advanced.ownership.remove.modal.aria_label"
              )
            }}
          />
        {{/if}}

        {{#if this.canChooseNewOwner}}
          <label
            for="circles-manage-advanced-new-owner-chooser"
            class="control-label"
          >
            {{i18n
              "discourse_circles.circle.manage.advanced.ownership.add.modal.label"
            }}
          </label>
          <UserChooser
            @id="circles-manage-advanced-new-owner-chooser"
            @value={{this.newOwner}}
            @onChange={{fn (mut this.newOwner)}}
            @options={{hash
              headerAriaLabel=(i18n
                "discourse_circles.circle.manage.advanced.ownership.add.modal.aria_label"
              )
              maximum=1
              excludeCurrentUser=false
              groupMembersOf=this.groups
              caretDownIcon="magnifying-glass"
            }}
          />
        {{/if}}

        {{#if this.warningMessage}}
          <p class="circles-change-ownership-warning">
            {{trustHTML this.warningMessage}}
          </p>
        {{/if}}

        {{#if this.removeStaffMembershipLabel}}
          <label class="control-label circles-remove-staff-membership">
            <Input
              @type="checkbox"
              @checked={{this.removeOldOwnerFromStaff}}
              name="remove-user-from-staff"
            />
            <span>
              {{trustHTML this.removeStaffMembershipLabel}}
            </span>
          </label>
        {{/if}}
      </:body>
      <:footer>
        <DButton
          @action={{this.save}}
          @disabled={{this.disableSubmit}}
          @label="submit"
          id="circles-manage-advanced-ownership-save"
          class="btn-danger"
        />
        <DButton @action={{this.close}} @label="cancel" />
      </:footer>
    </DModal>
  </template>
}
